import {
    Button,
    ButtonVariants,
    CustomClaimValue,
    Devices,
    FHWAClasses,
    getFHWAClassesDisplayName,
    getSceneTypeDisplayName,
    getTargetTypesDisplayName,
    getTimeOfDayDisplayName,
    getWeatherTypesDisplayName,
    OptionValue,
    Organizations,
    SceneType,
    TargetTypes,
    TimeOfDay,
    useAuth,
    WeatherTypes,
} from "@app/shared";
import { FC, ReactNode, useCallback, useMemo, useState } from "react";
import { useFetch } from "../../../../hooks";
import { CheckboxInput, DateInput, EnumSelect, FlagEnumCheckboxInput, Input, InputWithOptions, NumericInput } from "../../../shared/inputs";

export interface PhotoFilterValues {
    organizationId?: string;
    deviceId?: string;
    weather?: WeatherTypes;
    timeOfDay?: TimeOfDay;
    scene?: SceneType;
    targetTypesInView?: TargetTypes;
    fhwaClassesInView?: FHWAClasses;
    dewarpedImage?: boolean;
    needsReview?: boolean;
    numLanes?: number;
    dateFrom?: Date;
    dateTo?: Date;
    minWidth?: number;
    maxWidth?: number;
    minHeight?: number;
    maxHeight?: number;
    addedToTrainingDataset?: boolean;
    trainingDatasetName?: string;
}

interface Props {
    filters: PhotoFilterValues;
    onChange: (filters: PhotoFilterValues) => void;
}

const FilterSection: FC<{
    title: string;
    summary?: string;
    hasActiveFilter: boolean;
    children: ReactNode;
}> = ({ title, summary, hasActiveFilter, children }) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <div className="border-b border-gray-200 pb-2">
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="flex items-center justify-between w-full text-left py-1.5 group"
            >
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider group-hover:text-gray-700">
                    {title}
                    {hasActiveFilter && <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-blue-500" />}
                </span>
                <svg
                    className={`w-3.5 h-3.5 text-gray-400 transition-transform ${expanded ? "rotate-180" : ""}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {!expanded && hasActiveFilter && summary && (
                <p className="text-xs text-blue-600 truncate pb-1">{summary}</p>
            )}
            {expanded && <div className="pt-1 pb-1">{children}</div>}
        </div>
    );
};

function flagSummary(value: number | undefined, getDisplayName: (v: number) => string | undefined, enumObj: Record<string, number | string>): string | undefined {
    if (value === undefined || value === 0) return undefined;
    const names: string[] = [];
    for (const [, val] of Object.entries(enumObj)) {
        if (typeof val === "number" && val > 0 && (value & val) !== 0) {
            names.push(getDisplayName(val) ?? String(val));
        }
    }
    return names.join(", ") || undefined;
}

export const PhotoFilters: FC<Props> = ({ filters, onChange }) => {
    const auth = useAuth();
    const { currentOrganizationId } = auth;
    const isSuperAdmin = !!auth.userClaims?.find(uc => uc.claims.find(c => c === CustomClaimValue.SuperAdmin));

    const [orgOptions] = useFetch(Organizations.getAllOptions);

    const deviceOrgId = isSuperAdmin ? filters.organizationId : currentOrganizationId;
    const [devices] = useFetch(
        () => isSuperAdmin && !filters.organizationId
            ? Devices.getAllDevices()
            : Devices.getByOrganizationId(deviceOrgId),
        [isSuperAdmin, deviceOrgId]
    );

    const deviceOptions: OptionValue[] = useMemo(() =>
        (devices ?? []).map((d) => ({
            label: d.facilityName ? `${d.name} – ${d.facilityName}` : (d.name ?? ""),
            value: d.id,
        })),
        [devices]
    );

    const selectedOrgLabel = useMemo(
        () => orgOptions?.find((o) => o.value === filters.organizationId)?.label,
        [orgOptions, filters.organizationId]
    );

    const selectedDeviceLabel = useMemo(
        () => deviceOptions.find((o) => o.value === filters.deviceId)?.label,
        [deviceOptions, filters.deviceId]
    );

    const update = useCallback(
        (partial: Partial<PhotoFilterValues>) => onChange({ ...filters, ...partial }),
        [filters, onChange]
    );

    const handleOrgChange = useCallback(
        (orgId: string | undefined) => {
            onChange({ ...filters, organizationId: orgId, deviceId: undefined });
        },
        [filters, onChange]
    );

    const clearAll = () => onChange({});

    const hasActiveFilters = Object.values(filters).some((v) => v !== undefined);

    return (
        <div className="space-y-1">
            <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-700">Filters</h3>
                {hasActiveFilters && (
                    <Button variant={ButtonVariants.Secondary} onClick={clearAll}>Clear All</Button>
                )}
            </div>

            {isSuperAdmin && (
                <FilterSection
                    title="Organization"
                    hasActiveFilter={filters.organizationId !== undefined}
                    summary={selectedOrgLabel}
                >
                    <InputWithOptions
                        options={orgOptions ?? []}
                        value={filters.organizationId}
                        onChange={handleOrgChange}
                        placeholder="All organizations..."
                    />
                </FilterSection>
            )}

            <FilterSection
                title="Device"
                hasActiveFilter={filters.deviceId !== undefined}
                summary={selectedDeviceLabel}
            >
                <InputWithOptions
                    options={deviceOptions}
                    value={filters.deviceId}
                    onChange={(val) => update({ deviceId: val })}
                    placeholder="Search by device or location..."
                />
            </FilterSection>

            <FilterSection
                title="Date Range"
                hasActiveFilter={filters.dateFrom !== undefined || filters.dateTo !== undefined}
                summary={[
                    filters.dateFrom ? `From ${filters.dateFrom.toLocaleString()}` : undefined,
                    filters.dateTo ? `To ${filters.dateTo.toLocaleString()}` : undefined,
                ].filter(Boolean).join(" - ")}
            >
                <div className="grid grid-cols-2 gap-2">
                    <DateInput
                        label="From"
                        value={filters.dateFrom?.toISOString()}
                        onChange={(date) => update({ dateFrom: date ?? undefined })}
                        showTimeSelect={true}
                        showTimeInput={false}
                        timeIntervals={15}
                        dateFormat="MM/dd/yyyy HH:mm"
                    />
                    <DateInput
                        label="To"
                        value={filters.dateTo?.toISOString()}
                        onChange={(date) => update({ dateTo: date ?? undefined })}
                        showTimeSelect={true}
                        showTimeInput={false}
                        timeIntervals={15}
                        dateFormat="MM/dd/yyyy HH:mm"
                    />
                </div>
            </FilterSection>

            <FilterSection
                title="Time of Day"
                hasActiveFilter={filters.timeOfDay !== undefined}
                summary={filters.timeOfDay !== undefined ? getTimeOfDayDisplayName(filters.timeOfDay) : undefined}
            >
                <EnumSelect
                    enumType={TimeOfDay}
                    value={filters.timeOfDay}
                    onChange={(val) => update({ timeOfDay: val as TimeOfDay | undefined })}
                    emptyOptionText="All"
                    emptyOptionDisabled={false}
                    nameToDisplayConverter={getTimeOfDayDisplayName}
                    sort
                />
            </FilterSection>

            <FilterSection
                title="Scene"
                hasActiveFilter={filters.scene !== undefined}
                summary={filters.scene !== undefined ? getSceneTypeDisplayName(filters.scene) : undefined}
            >
                <EnumSelect
                    enumType={SceneType}
                    value={filters.scene}
                    onChange={(val) => update({ scene: val as SceneType | undefined })}
                    emptyOptionText="All Scenes"
                    emptyOptionDisabled={false}
                    nameToDisplayConverter={getSceneTypeDisplayName}
                    sort
                />
            </FilterSection>

            <FilterSection
                title="Weather"
                hasActiveFilter={filters.weather !== undefined}
                summary={flagSummary(filters.weather, getWeatherTypesDisplayName, WeatherTypes as any)}
            >
                <FlagEnumCheckboxInput
                    enumType={WeatherTypes}
                    value={filters.weather}
                    onChange={(val) => update({ weather: val })}
                    keyFilter={(k) => typeof k === "number" && k > 0}
                    nameToDisplayConverter={getWeatherTypesDisplayName}
                    sort
                />
            </FilterSection>

            <FilterSection
                title="Target Types In View"
                hasActiveFilter={filters.targetTypesInView !== undefined}
                summary={flagSummary(filters.targetTypesInView, getTargetTypesDisplayName, TargetTypes as any)}
            >
                <FlagEnumCheckboxInput
                    enumType={TargetTypes}
                    value={filters.targetTypesInView}
                    onChange={(val) => update({ targetTypesInView: val })}
                    keyFilter={(k) => typeof k === "number" && k > 0}
                    nameToDisplayConverter={getTargetTypesDisplayName}
                    sort
                />
            </FilterSection>

            <FilterSection
                title="FHWA Classes In View"
                hasActiveFilter={filters.fhwaClassesInView !== undefined}
                summary={flagSummary(filters.fhwaClassesInView, getFHWAClassesDisplayName, FHWAClasses as any)}
            >
                <FlagEnumCheckboxInput
                    enumType={FHWAClasses}
                    value={filters.fhwaClassesInView}
                    onChange={(val) => update({ fhwaClassesInView: val })}
                    keyFilter={(k) => typeof k === "number" && k > 0}
                    nameToDisplayConverter={getFHWAClassesDisplayName}
                />
            </FilterSection>

            <FilterSection
                title="Flags"
                hasActiveFilter={filters.needsReview !== undefined || filters.dewarpedImage !== undefined}
                summary={[
                    filters.needsReview ? "Needs Review" : undefined,
                    filters.dewarpedImage ? "Dewarped" : undefined,
                ].filter(Boolean).join(", ")}
            >
                <div className="flex items-center gap-4">
                    <CheckboxInput
                        label="Needs Review"
                        checked={filters.needsReview === true}
                        onChange={(e) => update({ needsReview: (e.target as HTMLInputElement).checked ? true : undefined })}
                    />
                    <CheckboxInput
                        label="Dewarped"
                        checked={filters.dewarpedImage === true}
                        onChange={(e) => update({ dewarpedImage: (e.target as HTMLInputElement).checked ? true : undefined })}
                    />
                </div>
            </FilterSection>

            <FilterSection
                title="Num Lanes"
                hasActiveFilter={filters.numLanes !== undefined}
                summary={filters.numLanes !== undefined ? `${filters.numLanes} lanes` : undefined}
            >
                <NumericInput
                    value={filters.numLanes ?? ""}
                    onChange={(val) => update({ numLanes: val })}
                    placeholder="Any"
                    min={0}
                    allowFloat={false}
                />
            </FilterSection>

            <FilterSection
                title="Training Dataset"
                hasActiveFilter={filters.addedToTrainingDataset !== undefined || !!filters.trainingDatasetName}
                summary={[
                    filters.addedToTrainingDataset === true ? "Added" : filters.addedToTrainingDataset === false ? "Not added" : undefined,
                    filters.trainingDatasetName ? `Name: ${filters.trainingDatasetName}` : undefined,
                ].filter(Boolean).join(", ")}
            >
                <div className="space-y-2">
                    <InputWithOptions
                        options={[
                            { label: "Added", value: "true" },
                            { label: "Not added", value: "false" },
                        ]}
                        value={filters.addedToTrainingDataset === undefined ? undefined : String(filters.addedToTrainingDataset)}
                        onChange={(val) => update({ addedToTrainingDataset: val === "true" ? true : val === "false" ? false : undefined })}
                        placeholder="Any"
                    />
                    <Input
                        label="Dataset name"
                        value={filters.trainingDatasetName ?? ""}
                        onChange={(e) => update({ trainingDatasetName: e.target.value || undefined })}
                        placeholder="e.g. light_rail"
                        maxLength={100}
                    />
                </div>
            </FilterSection>

            <FilterSection
                title="Image Size"
                hasActiveFilter={filters.minWidth !== undefined || filters.maxWidth !== undefined || filters.minHeight !== undefined || filters.maxHeight !== undefined}
                summary={[
                    filters.minWidth !== undefined || filters.maxWidth !== undefined
                        ? `W: ${filters.minWidth ?? "any"}-${filters.maxWidth ?? "any"}`
                        : undefined,
                    filters.minHeight !== undefined || filters.maxHeight !== undefined
                        ? `H: ${filters.minHeight ?? "any"}-${filters.maxHeight ?? "any"}`
                        : undefined,
                ].filter(Boolean).join(", ")}
            >
                <div className="grid grid-cols-2 gap-2">
                    <NumericInput
                        label="Min Width"
                        value={filters.minWidth ?? ""}
                        onChange={(val) => update({ minWidth: val })}
                        placeholder="Any"
                        min={0}
                        allowFloat={false}
                    />
                    <NumericInput
                        label="Max Width"
                        value={filters.maxWidth ?? ""}
                        onChange={(val) => update({ maxWidth: val })}
                        placeholder="Any"
                        min={0}
                        allowFloat={false}
                    />
                    <NumericInput
                        label="Min Height"
                        value={filters.minHeight ?? ""}
                        onChange={(val) => update({ minHeight: val })}
                        placeholder="Any"
                        min={0}
                        allowFloat={false}
                    />
                    <NumericInput
                        label="Max Height"
                        value={filters.maxHeight ?? ""}
                        onChange={(val) => update({ maxHeight: val })}
                        placeholder="Any"
                        min={0}
                        allowFloat={false}
                    />
                </div>
            </FilterSection>
        </div>
    );
};
